import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader } from 'lucide-react';
import { Ban } from '../types/player';

interface EditBanModalProps {
  ban: Ban;
  onClose: () => void;
  onSave: (updatedReason: string, updatedExpires: number | null) => void;
  isLoading: boolean;
}

// Helper function to convert Unix timestamp to a format suitable for datetime-local input
const toDateTimeLocal = (timestamp: number | null): string => {
  if (timestamp === null) return '';
  const date = new Date(timestamp * 1000);
  const ten = (i: number) => (i < 10 ? '0' : '') + i;
  return `${date.getFullYear()}-${ten(date.getMonth() + 1)}-${ten(date.getDate())}T${ten(date.getHours())}:${ten(date.getMinutes())}`;
};

export const EditBanModal = ({ ban, onClose, onSave, isLoading }: EditBanModalProps) => {
  const [reason, setReason] = useState(ban.reason);
  const [expires, setExpires] = useState(toDateTimeLocal(ban.expires));
  const [isPermanent, setIsPermanent] = useState(ban.expires === null);

  const handleSave = () => {
    const newExpires = isPermanent ? null : (expires ? new Date(expires).getTime() / 1000 : null);
    onSave(reason, newExpires);
  };

  const handlePermanenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsPermanent(e.target.checked);
    if (e.target.checked) {
      setExpires('');
    } else {
      // Set a default future date if it was permanent before
      setExpires(toDateTimeLocal(ban.expires || (Date.now() / 1000) + 86400));
    }
  };

  const modalContent = (
    <div className="modal-overlay z-50">
      <div className="modal-panel max-w-md">
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Edit Ban</h2>
            <p className="modal-subtitle font-mono text-xs">#{ban.ban_uuid.substring(0, 8)}</p>
          </div>
          <button type="button" onClick={onClose} className="modal-close" title="Close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="modal-body">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">Reason</label>
              <textarea
                className="glass-input w-full min-h-[8rem]"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">Expires</label>
              <input
                type="datetime-local"
                className="glass-input w-full tabular-nums"
                value={expires}
                onChange={(e) => setExpires(e.target.value)}
                disabled={isPermanent}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="permanent-ban"
                className="form-checkbox"
                checked={isPermanent}
                onChange={handlePermanenceChange}
              />
              <label htmlFor="permanent-ban" className="text-sm text-glass-300 cursor-pointer">
                Permanent
              </label>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="glass-button-secondary">
            Cancel
          </button>
          <button onClick={handleSave} className="btn-primary" disabled={isLoading}>
            {isLoading && <Loader className="w-4 h-4 animate-spin" />}
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
